import { Typography } from '@mui/material';
import { FormSelect, FormTextField } from 'src/components/Forms';
import { CreateDraftWorkflowVersionField } from 'src/pages/Settings/workflows/components/create-draft-version-form';

const DEFINITION_TEXTAREA_ROWS = 14;

const validateWorkflowDefinition = (value: string) => {
  if (!value.trim()) {
    return 'Definition is required';
  }

  try {
    JSON.parse(value);
    return true;
  } catch {
    return 'Invalid JSON format';
  }
};

interface WorkflowOption {
  workflowId: string;
  name: string;
}

interface CreateDraftWorkflowVersionFieldsProps {
  disabled: boolean;
  workflows: WorkflowOption[];
}

export const CreateDraftWorkflowVersionFields = ({
  disabled,
  workflows,
}: CreateDraftWorkflowVersionFieldsProps) => {
  const workflowOptions: Option[] = workflows.map((workflow) => ({
    id: workflow.workflowId,
    value: workflow.workflowId,
    label: workflow.name,
  }));

  return (
    <>
      <FormSelect
        type="select"
        name={CreateDraftWorkflowVersionField.WorkflowId}
        label="Workflow"
        required="Please select a workflow"
        disabled={disabled}
        options={workflowOptions}
        data-testid="create-draft-version-workflow"
        showErrorText
        selectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            if (!selected) {
              return (
                <Typography component="span" variant="inherit" color="text.secondary">
                  Select Workflow
                </Typography>
              );
            }

            return (
              workflowOptions.find((option) => option.value === selected)?.label ??
              String(selected)
            );
          },
        }}
      />

      <FormTextField
        type="textfield"
        name={CreateDraftWorkflowVersionField.Description}
        label="Description (Optional)"
        disabled={disabled}
        textFieldProps={{
          multiline: true,
          minRows: 2,
          maxRows: 4,
          placeholder: 'e.g., "Updated email trigger logic"',
          inputProps: { 'data-testid': 'create-draft-version-description-input' },
        }}
      />

      <FormTextField
        type="textfield"
        name={CreateDraftWorkflowVersionField.Definition}
        label="Workflow Definition (JSON)"
        disabled={disabled}
        rules={{ validate: validateWorkflowDefinition }}
        showErrorText
        textFieldProps={{
          multiline: true,
          rows: DEFINITION_TEXTAREA_ROWS,
          inputProps: {
            'data-testid': 'create-draft-version-definition-input',
            style: {
              fontFamily: 'IBM Plex Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
            },
          },
        }}
      />
    </>
  );
};
